import { Camera } from '@phosphor-icons/react';
import { Button, Loader, VText } from '@vake/ui-kit';

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    useGetInsights,
    useGetNewestInsight,
    useGetVesselPathfinderQuery,
    useGetVesselQuery,
    useGetVesselTagsQuery,
} from '../requests/actor-mode';
import { SmallMapWithMarker } from '../shared/components/MapComponents';
import { formatRelativeTime } from '../shared/components/time';
import { VakeClipboard } from '../utils/clipboard';
import { ErrorHandler } from '../utils/errorHandling';
import { convertDecimalToDM } from '../utils/location';
import { mmsiToFlag } from '../utils/mmsi-to-flag';
import { externalMarineTrafficByIMO } from '../utils/mmsiExternalCall';
import { InsightHolders } from './RecentInsightDashboard';
import { SendToMattermost } from './Report/components/send-to-mattermost';
import { TagView } from './Report/components/tags';

type RouteParams = {
    reportUuid: string;
    insightUuid: string;
};
const ActorModeVessel = () => {
    const params = useParams<RouteParams>();
    const reportUuid = params.reportUuid || '';
    const insightUuid = params.insightUuid || '';
    const { data: vessel, isError, isLoading } = useGetVesselQuery(insightUuid);

    const overwatchReport = `/overwatch/${reportUuid}`;
    const name = isLoading || !vessel || !vessel.name ? null : vessel.name;
    const imo = isLoading || !vessel || !vessel.imo ? null : vessel.imo;
    const mmsi = isLoading || !vessel ? null : vessel.mmsi;
    const lastUpdated = isLoading || !vessel ? '' : formatRelativeTime(new Date(vessel.lastUpdated));
    const lastPosition = isLoading || !vessel ? '' : vessel.lastPosition;
    const lastPositionDegrees = isLoading || !vessel ? '' : convertDecimalToDM(vessel.latitude, vessel.longitude);
    const {
        data: vesselTags,
        isLoading: loadingTags,
        isError: errorTags,
        isSuccess: tagsSuccess,
    } = useGetVesselTagsQuery(insightUuid);
    const {
        data: vesselPathfinders,
        isLoading: loadingPathfinders,
        isError: errorPathfinders,
        isSuccess: pathfindersSuccess,
    } = useGetVesselPathfinderQuery(mmsi || undefined);
    const {
        data: vesselInsightsInAOI,
        isLoading: loadingInsightsInAOI,
        isError: errorInsightsInAOI,
        isSuccess: insightsInAOISuccess,
    } = useGetInsights(mmsi, true, true, reportUuid, insightUuid);
    const [loadInsightsOutsideAOI, setLoadInsightsOutsideAOI] = useState<boolean>(false);
    const {
        data: vesselInsightsOutsideAOI,
        isLoading: loadingInsightsOutsideAOI,
        isError: errorInsightsOutsideAOI,
        isSuccess: insightsOutsideAOISuccess,
    } = useGetInsights(mmsi, loadInsightsOutsideAOI, false, reportUuid, insightUuid);
    const {
        data: newestInsight,
        isLoading: newestInsightLoading,
        isError: errorNewestInsight,
        isSuccess: insightsNewestSuccess,
    } = useGetNewestInsight(insightUuid);
    const newestOverwatchLink =
        newestInsightLoading || !newestInsight || isLoading || !vessel
            ? null
            : `${overwatchReport}?date=${new Date(newestInsight.timestamp).toDateString()}&confidence=92&uuid=${
                  newestInsight.insight_uuid
              }`;
    return (
        <div className="flex flex-col p-16 gap-16 max-w-[1536px] mx-auto">
            {isLoading && (
                <div className="absolute top-1/2 left-1/2 z-10">
                    <Loader size={16} />
                </div>
            )}
            <ErrorHandler isError={isError} errorMessage={'Loading vessel data failed'}>
                <VText>Error loading vessel data.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorTags} errorMessage={'Loading tags failed'}>
                <VText>Error loading tags.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorPathfinders} errorMessage={'Loading pathfinders failed'}>
                <VText>Error loading pathfinders.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorInsightsInAOI} errorMessage={'Loading insights in AOI failed'}>
                <VText>Error loading insights in AOI.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorInsightsOutsideAOI} errorMessage={'Loading insights outside AOI failed'}>
                <VText>Error loading insights outside AOI.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorNewestInsight} errorMessage={'Loading newest insight failed'}>
                <VText>Error loading newest insight.</VText>
            </ErrorHandler>
            <div className="flex items-center gap-4">
                <VText>{mmsiToFlag(mmsi)}</VText>
                <div className="flex items-center">
                    <VText variant="H1">Name: {name ? name : 'N/A'}</VText>
                    {name && <VakeClipboard infoValue={name} />}
                </div>
                <div className="flex items-center">
                    <VText variant="H1">IMO: {imo ? imo : 'N/A'}</VText>
                    {imo && <VakeClipboard infoValue={imo.toString()} />}
                </div>
                <div className="flex items-center">
                    <VText variant="H1">MMSI: {mmsi ? mmsi : 'N/A'}</VText>
                    {mmsi && <VakeClipboard infoValue={mmsi.toString()} />}
                </div>
            </div>
            <div className="flex flex-col gap-2 max-w-80">
                <VText>Last Updated: {lastUpdated}</VText>
                <div className="flex items-center">
                    <VText>Last Position (lat, lon): {lastPosition}</VText>
                    <VakeClipboard infoValue={lastPosition} />
                </div>
                <div className="flex item-center">
                    <VText>Last Position: {lastPositionDegrees}</VText>
                    <VakeClipboard infoValue={lastPositionDegrees} />
                </div>
                <SmallMapWithMarker lat={vessel?.latitude} lon={vessel?.longitude} />
                {newestInsightLoading && (
                    <div className="flex justify-center">
                        <Loader size={16} />
                    </div>
                )}
                {insightsNewestSuccess && newestOverwatchLink !== null && (
                    <Link to={newestOverwatchLink}>
                        <Button text="Open in Overwatch" />
                    </Link>
                )}
                <SendToMattermost
                    textOnButton="Order Satellite Image"
                    iconOnButton={<Camera size={16} />}
                    textToSend={`Ordering satellite image for vessel ${name} (IMO: ${imo}, MMSI: ${mmsi})`}
                    successMessage={`Thank you, we've received your order. We'll send you the image soon.`}
                />
                {imo && (
                    <Button
                        text="Open MarineTraffic"
                        variant="neutral"
                        onClick={() => window.open(externalMarineTrafficByIMO(imo), '_blank')}
                    />
                )}
            </div>
            <div className="flex flex-col gap-2 max-w-60">
                <VText>Actions last 6 months</VText>
                {loadingTags && (
                    <div className="flex justify-center">
                        <Loader size={16} />
                    </div>
                )}
                {tagsSuccess && (
                    <div className="flex gap-2">
                        <TagView tags={vesselTags && vesselTags.length > 0 ? vesselTags : ['Nothing Suspicious']} />
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-2 max-w-60">
                <VText>Pathfinder reports</VText>
                {loadingPathfinders && (
                    <div className="flex justify-center">
                        <Loader size={16} />
                    </div>
                )}
                {pathfindersSuccess && (
                    <div className="max-h-60 overflow-auto">
                        {vesselPathfinders && vesselPathfinders.length > 0 ? (
                            vesselPathfinders.map((pathfinder, index) => {
                                const pathfinderLink = pathfinder.endTime
                                    ? `/pathfinder/${pathfinder.url}`
                                    : `https://pathfinder.vake.ai/${pathfinder.uuid}`;
                                return (
                                    <Link to={pathfinderLink} key={index}>
                                        <Button
                                            text={`${pathfinder.name}: ${
                                                pathfinder.startTime?.toDateString() ?? 'N/A'
                                            } - ${pathfinder.endTime?.toDateString() ?? 'N/A'}`}
                                            variant="neutral"
                                        />
                                    </Link>
                                );
                            })
                        ) : (
                            <VText significance="secondary">No pathfinder reports available</VText>
                        )}
                    </div>
                )}
                <Link to={`/order-pathfinder-report?mmsi=${mmsi}&name=${name}`}>
                    <Button text="Order Pathfinder" />
                </Link>
            </div>
            <div>
                <VText>Images of vessel in AOI last 6 months</VText>
                {loadingInsightsInAOI && (
                    <div className="flex">
                        <Loader size={16} />
                    </div>
                )}
                {insightsInAOISuccess && vesselInsightsInAOI && <InsightHolders insights={vesselInsightsInAOI} />}
            </div>
            <div>
                <VText>Images of vessel outside AOI last 6 months</VText>
                {loadingInsightsOutsideAOI && (
                    <div className="flex">
                        <Loader size={16} />
                    </div>
                )}
                {loadInsightsOutsideAOI ? (
                    <>
                        {insightsOutsideAOISuccess && vesselInsightsOutsideAOI && (
                            <InsightHolders insights={vesselInsightsOutsideAOI} />
                        )}
                    </>
                ) : (
                    <Button text="Load" variant="neutral" onClick={() => setLoadInsightsOutsideAOI(true)} />
                )}
            </div>
            {/* {newestInsight && <ClearviewView uuid={newestInsight.insight_uuid} />} */}
            {/* <VText>AIS gaps</VText> */}
            {/* <VText>Flag history</VText> */}
        </div>
    );
};
export default ActorModeVessel;
